const citations = [
  { author: "Dale, 2019", text: "Ugly-cute embraces asymmetry, disproportion, visible seams, and exaggerated features." },
  { author: "Hagan, 2021", text: "Imperfection becomes character, and strangeness signals authenticity." },
  { author: "Schwartz, 2017", text: "Value is no longer defined solely by polish, but by expressiveness and the trace of human touch." },
];

export default function WhyImperfectionSection() {
  return (
    <section id="imperfection" className="bg-[#0D0D0D] py-24 md:py-36">
      <div className="w-full px-8 md:px-16">

        {/* Header */}
        <div className="flex items-center gap-4 mb-16">
          <span className="border border-white/20 text-white/60 font-mono text-[10px] uppercase tracking-[3px] px-3 py-1.5 rounded-full whitespace-nowrap">
            02 / 06
          </span>
          <div className="flex-1 h-[1px] bg-white/10" />
          <span className="font-mono text-[10px] text-white/30 uppercase tracking-[2px] whitespace-nowrap">
            Theoretical Framework
          </span>
        </div>

        {/* Title */}
        <h2
          className="font-display text-white leading-tight mb-16"
          style={{ fontSize: "clamp(42px, 6vw, 84px)" }}
        >
          Reframing Textile Waste<br />
          <span className="text-[#E63946]">Through Ugly-Cute</span>
        </h2>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">

          {/* Left: Body Text + Citations */}
          <div className="md:col-span-7 space-y-8">
            <p className="font-body text-white/70 text-lg leading-relaxed">
              In response to dominant standards of perfection, contemporary culture has witnessed the rise of
              &lsquo;ugly-cute&rsquo; aesthetics. Originating in Japanese kawaii culture, ugly-cute embraces
              asymmetry, disproportion, visible seams, and exaggerated features—elements traditionally
              considered flawed in Western design canons <span className="text-white/40 font-mono text-sm">(Dale, 2019)</span>.
              Rather than hiding irregularity, ugly-cute amplifies it. Imperfection becomes character,
              and strangeness signals authenticity <span className="text-white/40 font-mono text-sm">(Hagan, 2021)</span>.
            </p>
            <p className="font-body text-white/70 text-lg leading-relaxed">
              This shift signals a broader cultural change: value is no longer defined solely by polish
              and uniformity, but by expressiveness and the trace of human touch <span className="text-white/40 font-mono text-sm">(Schwartz, 2017)</span>.
              When applied to textile waste, this logic becomes transformative. Materials once rejected
              for their irregularity—offcuts, misprints, discoloured patches—can be reframed not as
              compromised resources but as expressive design components with inherent visual interest.
            </p>

            {/* Citation Cards */}
            <div className="space-y-3 pt-4">
              <p className="font-mono text-[10px] uppercase tracking-[3px] text-white/20 mb-4">
                Key References
              </p>
              {citations.map((c) => (
                <div
                  key={c.author}
                  className="flex gap-4 items-start bg-white/5 rounded-2xl px-5 py-4 border-l-2 border-[#E63946]"
                >
                  <span className="font-mono text-[10px] text-[#E63946] uppercase tracking-[2px] whitespace-nowrap mt-0.5">
                    {c.author}
                  </span>
                  <p className="font-body text-white/40 text-sm leading-relaxed italic">
                    &ldquo;{c.text}&rdquo;
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Image */}
          <div className="md:col-span-5">
            <div className="relative">
              {/* Annotation line */}
              <div className="absolute -left-6 top-1/2 -translate-y-1/2 hidden md:flex flex-col items-center gap-3 z-10">
                <div className="w-[2px] h-20 bg-[#E63946]" />
                <span
                  className="font-mono text-[10px] uppercase tracking-[3px] text-[#E63946] whitespace-nowrap"
                  style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                >
                  Ugly-Cute Reference
                </span>
                <div className="w-[2px] h-20 bg-[#E63946]" />
              </div>

              <div className="rounded-3xl overflow-hidden" style={{ aspectRatio: "4/5" }}>
                <img
                  src="https://static.readdy.ai/image/79bbd12133c9c5c423ad9964f66c8be6/ef629c881acceb494ed6105a5f12fee4.png"
                  alt="Ugly-cute character dolls reference"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* White offset border */}
              <div className="absolute -bottom-3 -right-3 w-full h-full rounded-3xl border-2 border-white/15 -z-10" />
            </div>

            {/* Caption */}
            <div className="mt-6 bg-white/5 rounded-2xl px-5 py-4">
              <p className="font-mono text-[10px] uppercase tracking-[3px] text-white/30 mb-1">
                Aesthetic Reference
              </p>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                Contemporary ugly-cute character design — asymmetry, exaggerated features, and deliberate strangeness as aesthetic language.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Marquee */}
        <div className="mt-20 py-6 border-t border-b border-white/10 overflow-hidden">
          <div
            className="flex gap-12 whitespace-nowrap"
            style={{ animation: "marquee 24s linear infinite" }}
          >
            {Array(8).fill(null).map((_, i) => (
              <span key={i} className="font-display text-3xl text-white/15 uppercase tracking-widest">
                Ugly-Cute &nbsp;/&nbsp; Beyond the Standard &nbsp;/&nbsp; Waste as Wonder &nbsp;/
              </span>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
